import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import AddressFields from 'components/address/AddressFields';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  RecordNote,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { AddressErrors, emptyAddress, isAddressFilled, validateAddress } from 'helpers/addressValidation';
import { documentLabel } from 'helpers/documentFormat';
import { parseMoney } from 'helpers/moneyFormat';
import { api } from 'services/api';
import history from 'services/history';
import { Bank } from 'types/bank';
import { Supplier, SupplierAddress, SupplierBank, SupplierProduct } from 'types/supplier';
import SupplierBanks from '../SupplierBanks';
import SupplierProducts from '../SupplierProducts';
import SupplierForm from '../form/SupplierForm';
import SupplierTab from '../tab/SupplierTab';
import { useSupplierValidation } from '../validation/useSupplierValidation';

// todos os campos validados ficam na aba "Dados do fornecedor"
const FORM_TAB = 'main';

const EditSupplier: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [banks, setBanks] = useState<Bank[]>([]);
  const [validation, setValidation, validate] = useSupplierValidation();
  const [tab, setTab] = useState(FORM_TAB);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [clearingAddress, setClearingAddress] = useState(false);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [savedName, setSavedName] = useState('');
  const [savedActive, setSavedActive] = useState(true);
  const [supplierAddress, setSupplierAddress] = useState<SupplierAddress | null>(null);
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});

  const loadSupplier = useCallback(() => {
    if (!id) return Promise.resolve();

    return api
      .get(`/suppliers/${id}`)
      .then(response => {
        const supplierData = response.data;
        setSupplier(supplierData);
        setSupplierAddress(supplierData.address ?? { ...emptyAddress, supplier_id: supplierData.id });
        setSavedName(supplierData.company_trade_name || supplierData.company_name);
        setSavedActive(!!supplierData.active);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar os dados do fornecedor');
        history.push('/suppliers');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadSupplier().finally(() => setLoading(false));
  }, [loadSupplier]);

  useEffect(() => {
    fetch('https://brasilapi.com.br/api/banks/v1')
      .then(response => response.json())
      .then(data => setBanks(data.filter((item: Bank) => !!item.code)))
      .catch(error => console.error('Error fetching bank data:', error));
  }, []);

  // a validação guarda o erro do campo; abre a aba onde ele está
  useEffect(() => {
    if (Object.keys(validation).length > 0) setTab(FORM_TAB);
  }, [validation]);

  function goBack() {
    history.push('/suppliers');
  }

  function handleChange<K extends keyof Supplier>(index: K, value: Supplier[K]) {
    setSupplier(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleAddressChange(address: SupplierAddress) {
    setSupplierAddress(address);
    setAddressErrors({});
  }

  function handleSubmit() {
    if (!supplier) return;

    setSaving(true);

    const address = supplierAddress;
    const data = {
      ...supplier,
      address:
        address && isAddressFilled(address)
          ? {
              zip_code: address.zip_code,
              street: address.street,
              neighborhood: address.neighborhood,
              city: address.city,
              state: address.state,
              street_number: address.street_number,
              complement: address.complement,
            }
          : null,
      // contas e produtos são gravados na hora, em suas abas
      banks: [],
      products: [],
    };

    api
      .put(`/suppliers/${supplier.id}`, data)
      .then(() => loadSupplier())
      .then(() => toast.success('Fornecedor atualizado'))
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar o fornecedor');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!supplier) return;

    validate(supplier)
      .then(() => {
        const errors = validateAddress(supplierAddress);
        setAddressErrors(errors);

        if (Object.keys(errors).length > 0) {
          setTab('address');
          toast.error('Corrija o endereço para salvar');
          return;
        }

        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  // "excluir" só inativa o fornecedor; produtos e pacotes dele continuam cadastrados
  function handleInactivateSupplier() {
    if (!supplier) return;

    setSaving(true);

    api
      .delete(`/suppliers/${supplier.id}`)
      .then(() => {
        toast.success('Fornecedor inativado com sucesso!');
        history.push('/suppliers');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível inativar o fornecedor');
      })
      .finally(() => {
        setSaving(false);
        setConfirmDialogOpen(false);
      });
  }

  function handleClearAddress() {
    if (!supplier) return;
    setClearingAddress(true);

    api
      .delete(`/suppliers/${supplier.id}/address`)
      .then(() => {
        setSupplierAddress({ ...emptyAddress, supplier_id: supplier.id });
        setAddressErrors({});
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível limpar o endereço do fornecedor');
      })
      .finally(() => setClearingAddress(false));
  }

  // produtos e contas bancárias são gravados na hora, enviando só o item alterado
  function saveList(extra: Partial<Pick<Supplier, 'products' | 'banks'>>, failure: string) {
    if (!supplier) return;

    api
      .put(`/suppliers/${supplier.id}`, { ...supplier, address: null, banks: [], products: [], ...extra })
      .then(response => {
        toast.success('Salvo');
        setSupplier(state =>
          state ? { ...state, products: response.data.products, banks: response.data.banks } : null,
        );
      })
      .catch(err => {
        toast.error(err.response?.data?.error || failure);
      });
  }

  function handleChangeAddBank(bank: SupplierBank) {
    saveList({ banks: [bank] }, 'Não foi possível adicionar a conta bancária');
  }

  function handleDeleteBank(bank: SupplierBank) {
    saveList({ banks: [{ ...bank, active: 0 } as SupplierBank] }, 'Não foi possível excluir a conta bancária');
  }

  function handleChangeAddProduct(product: SupplierProduct) {
    saveList(
      {
        products: [
          {
            ...product,
            active: 1,
            cost_price: parseMoney(product.cost_price),
            sale_price_max: parseMoney(product.sale_price_max),
            sale_price_min: parseMoney(product.sale_price_min),
          } as SupplierProduct,
        ],
      },
      'Não foi possível adicionar o produto ao fornecedor',
    );
  }

  function handleDeleteProduct(product: SupplierProduct) {
    saveList({ products: [{ ...product, active: 0 } as SupplierProduct] }, 'Não foi possível excluir o produto');
  }

  const isEditing = tab === FORM_TAB || tab === 'address';

  return (
    <>
      <Appbar title="Fornecedores" backAction={goBack} />

      {saving && <InsideSaving />}

      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Inativar"
          destructive
          handleNoClick={() => setConfirmDialogOpen(false)}
          handleYesClick={handleInactivateSupplier}
          title="Deseja inativar esse fornecedor?"
        />
      )}

      <PageShell>
        <RecordHeader title={savedName || 'Fornecedor'} active={savedActive} backAction={goBack}>
          {supplier?.document_number && (
            <Chip
              size="small"
              variant="outlined"
              label={documentLabel(supplier.person_type, supplier.document_number)}
            />
          )}
          {supplier?.category && (
            <Chip
              size="small"
              variant="outlined"
              title={supplier.category}
              label={supplier.category.length > 40 ? `${supplier.category.slice(0, 40)}…` : supplier.category}
            />
          )}
        </RecordHeader>

        <WorkArea>
          <SupplierTab
            onChange={newTab => setTab(newTab)}
            tab={tab}
            productsCount={supplier?.products.length}
            banksCount={supplier?.banks.length}
          />
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !supplier ? (
              <NoData message="Não foi possível carregar os dados do fornecedor" />
            ) : tab === 'products' ? (
              <>
                <SupplierProducts
                  supplier={supplier}
                  handleDeleteProduct={handleDeleteProduct}
                  handleChangeAddProduct={handleChangeAddProduct}
                />
                <RecordNote>Adicionar, editar e excluir produto salva na hora.</RecordNote>
              </>
            ) : tab === 'banks' ? (
              <>
                <SupplierBanks
                  handleDeleteBank={handleDeleteBank}
                  banks={banks}
                  supplier={supplier}
                  handleChangeAddBank={handleChangeAddBank}
                />
                <RecordNote>Adicionar, editar e excluir conta salva na hora.</RecordNote>
              </>
            ) : (
              <StyledForm onSubmit={handleValidation}>
                {tab === 'address' ? (
                  <AddressFields
                    address={supplierAddress}
                    onChange={handleAddressChange}
                    errors={addressErrors}
                    onClear={handleClearAddress}
                    clearing={clearingAddress}
                  />
                ) : (
                  <SupplierForm handleChange={handleChange} supplier={supplier} validation={validation} />
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {isEditing && !loading && supplier && (
          <RecordFooter sx={{ justifyContent: 'space-between' }}>
            <Button color="error" size="small" disabled={saving} onClick={() => setConfirmDialogOpen(true)}>
              Inativar fornecedor
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
                Salvar alterações
              </Button>
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditSupplier;
